import { useState, useEffect } from 'react';
import API from '../api/axios';
import TaskItem from '../components/TaskItem';
import TaskModal from '../components/TaskModal';
import useSortPreference from '../hooks/useSortPreference';
import useHideCompletedPreference from '../hooks/useHideCompletedPreference';

const priorityOrder = { Low: 0, Medium: 1, High: 2 };
const statusOrder = { 'Not Started': 0, Preparing: 1, 'In Progress': 2, 'On Hold': 3, Completed: 4 };

function compareSortValues(a, b, sortCol, sortDir) {
  let valA, valB;
  switch (sortCol) {
    case 'task_number':
      valA = a.task_number ?? null; valB = b.task_number ?? null;
      break;
    case 'name':
      valA = (a.name || '').toLowerCase(); valB = (b.name || '').toLowerCase();
      break;
    case 'folder_name':
      valA = (a.folder_name || '').toLowerCase(); valB = (b.folder_name || '').toLowerCase();
      break;
    case 'priority':
      valA = a.priority != null ? priorityOrder[a.priority] : null;
      valB = b.priority != null ? priorityOrder[b.priority] : null;
      break;
    case 'status':
      valA = a.status != null ? statusOrder[a.status] : null;
      valB = b.status != null ? statusOrder[b.status] : null;
      break;
    case 'start_date':
      valA = a.start_date ? new Date(a.start_date) : null;
      valB = b.start_date ? new Date(b.start_date) : null;
      break;
    case 'end_date':
      valA = a.end_date ? new Date(a.end_date) : null;
      valB = b.end_date ? new Date(b.end_date) : null;
      break;
    default:
      return 0;
  }
  if (valA == null && valB == null) return 0;
  if (valA == null) return 1;
  if (valB == null) return -1;
  if (valA < valB) return sortDir === 'asc' ? -1 : 1;
  if (valA > valB) return sortDir === 'asc' ? 1 : -1;
  return 0;
}

export default function FocusPage() {
  const [tasks, setTasks] = useState([]);
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [hideCompleted, setHideCompleted] = useHideCompletedPreference('focusly-focus-hide-completed');
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const { sortCol, sortDir, handleSort } = useSortPreference('focusly-focus-sort');

  const fetchTasks = async () => {
    const { data } = await API.get('/tasks/focus');
    setTasks(data);
  };

  useEffect(() => {
    fetchTasks();
    const fetchFolders = async () => {
      const { data } = await API.get('/folders');
      setFolders(data);
    };
    const fetchTags = async () => {
      const { data } = await API.get('/tags');
      setTags(data);
    };
    fetchFolders();
    fetchTags();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await API.delete(`/tasks/${id}`);
    setShowModal(false);
    fetchTasks();
  };

  const handleEdit = (task) => {
    setEditTask(task);
    setShowModal(true);
  };

  const handleSave = async (taskData) => {
    await API.put(`/tasks/${editTask.id}`, taskData);
    setShowModal(false);
    fetchTasks();
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Focus</h4>
        <button
          className={`btn btn-sm ${hideCompleted ? 'btn-outline-secondary' : 'btn-secondary'}`}
          onClick={() => setHideCompleted(!hideCompleted)}
        >
          {hideCompleted ? 'Show Completed' : 'Hide Completed'}
        </button>
      </div>

      {(() => {
        const base = hideCompleted ? tasks.filter((t) => t.status !== 'Completed') : tasks;
        const filtered = sortCol
          ? [...base].sort((a, b) => compareSortValues(a, b, sortCol, sortDir))
          : base;

        const sortIndicator = (col) => {
          if (sortCol !== col) return ' \u2195';
          return sortDir === 'asc' ? ' \u2191' : ' \u2193';
        };

        return filtered.length === 0 ? (
          <p className="text-muted">{hideCompleted ? 'No incomplete tasks with due dates.' : 'No tasks with due dates.'}</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Type</th>
                  <th style={{ width: 60, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('task_number')}>ID{sortIndicator('task_number')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>Name{sortIndicator('name')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('folder_name')}>Folder{sortIndicator('folder_name')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('priority')}>Priority{sortIndicator('priority')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>Status{sortIndicator('status')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('start_date')}>Start{sortIndicator('start_date')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('end_date')}>End{sortIndicator('end_date')}</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <TaskItem key={t.id} task={t} onEdit={handleEdit} showFolder />
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {showModal && (
        <TaskModal
          task={editTask}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setShowModal(false)}
          folders={folders}
          currentFolderId={editTask?.folder_id}
          tags={tags}
        />
      )}
    </div>
  );
}
